/* Distribution — a faithful React port of the prototype's rMarkets screen.
 *
 * The world of country markets as a sortable, filterable table: per country your
 * go-to-market methods, brand rating, coverage, last-quarter share, market
 * control and the rivals' footprints. Clicking a country opens a management modal
 * — segment potential, distribution methods (online is always on; distributor /
 * carrier / own-stores are activated or cancelled), an own-store network panel
 * (build/flagship) and competitor intel.
 *
 * Distribution levers are staged in the decisions draft (store.toggleDistribution
 * → d.distribution, store.setStoreBuild → d.storeBuilds, store.toggleFlagship →
 * d.flagship) and previewed live through buildWorkingGame, which replays the same
 * intents the server's applyDecisions runs at advance (enterMethod / buildStores /
 * setFlagship) — so coverage, control and store reach on screen are exactly what
 * resolves. Sub-sections are plain render functions (not nested components) so
 * React reconciles in place and the page never scroll-jumps. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import {
  Engine, applyGameCfg, SEGMENTS, METHODS, REGIONS, PROFILES, COUNTRIES, COMPETITORS,
  FLAGSHIP_BRAND, FLAGSHIP_COST, fmtK, fmtM, fmt$, clamp,
} from '@venturewrist/engine';
import type { DistMethod } from '@venturewrist/shared';
import { useDecisions, distHas, storeBuildOf, storeCloseOf, flagshipHas } from '../../store/decisions';
import { buildWorkingGame } from '../../lib/designState';
import { Flag } from '../../components/Flag';
import type { ScreenProps } from './types';
import '../../styles/distribution.css';

const Eng = Engine as any;
const SEGS = SEGMENTS as any[];
const METHS = METHODS as any[];
const REGS = REGIONS as any[];
const PROFS = PROFILES as any;
const CTRYS = COUNTRIES as any[];
const COMPALL = COMPETITORS as any[];
const STORE_BUILD_LIMIT = 8;

const WhyChip = () => <span className="tooltip-why" title="Learn the theory">?</span>;
const Bar = ({ v, color }: { v: number; color?: string }) => (
  <div className="bar-track"><div className="bar-fill" style={{ width: clamp(v, 0, 100) + '%', background: color || undefined }} /></div>
);
const ctrlChip = (ctrl: number, full = 'Full', partial = 'Partial', dist = 'Distributor') =>
  ctrl > 0.85 ? <span className="chip good">{full}</span> : ctrl > 0.5 ? <span className="chip warn">{partial}</span> : <span className="chip bad">{dist}</span>;
const methodsStr = (m: any) =>
  '🌐' + (m?.dist ? ' 🤝' : '') + (m?.sub ? ' 🏪' + (m.n || 0) + (m.flagship ? '⭐' : '') : '') + (m?.carrier ? ' 📡' : '');

type Filter = 'all' | 'mine' | 'open';
type Sort = 'size' | 'pop' | 'brand' | 'cov' | 'share';

export function Distribution({ detail }: ScreenProps) {
  const draft = useDecisions((z) => z.draft);
  const toggleDistribution = useDecisions((z) => z.toggleDistribution);
  const setStoreBuild = useDecisions((z) => z.setStoreBuild);
  const setStoreClose = useDecisions((z) => z.setStoreClose);
  const toggleFlagship = useDecisions((z) => z.toggleFlagship);

  const [f, setF] = useState<Filter>('all');
  const [region, setRegion] = useState<string>('all');
  const [sort, setSort] = useState<Sort>('size');
  const [q, setQ] = useState('');
  const [openCC, setOpenCC] = useState<string | null>(null);

  const g = useMemo(() => buildWorkingGame(detail.state, draft), [detail.state, draft]);
  applyGameCfg(g);

  const round = g.round as number;
  const server = detail.state as any;
  const last = (server.history ?? []).at(-1);
  const unique = Eng.isUnique(g) as boolean;
  const models = Eng.activeModels(g) as any[];
  const co = unique ? null : Eng.ensureBrandCo(g);

  const active = g.countries && g.countries.length ? CTRYS.filter((c) => g.countries.includes(c.id)) : CTRYS;
  const nComps = g.numComps ? Math.max(1, Math.min(g.numComps, COMPALL.length)) : 3;
  const comps = COMPALL.slice(0, nComps);

  // ── derived per-country helpers ───────────────────────────────────────────
  const brandOf = (c: any): number => {
    const prof = PROFS[c.profile];
    const rate = (e: any) => Eng.brandRating(e.loyal, SEGS.reduce((a, s) => a + (e.aw[s.id] || 0) * prof[s.id], 0));
    if (!unique) return rate(co[c.id]);
    if (!models.length) return 0;
    return Math.max(...models.map((mm) => rate(Eng.pBrandCo(g, mm)[c.id])));
  };
  const shareOf = (cc: string): number | null => {
    const cm = last && last.byCountry && last.byCountry[cc];
    return cm && cm.demand > 0 ? cm.units / cm.demand : null;
  };
  const controlOf = (c: any): number => {
    let dw = 0, ctw = 0;
    for (const s of SEGS) { const d = Eng.segDemand(c, s.id, round), mt = Eng.covMeta(g, c, s.id, round); dw += d * mt.cov; ctw += d * mt.cov * mt.ctrl; }
    return dw ? ctw / dw : 1;
  };

  // ── KPI strip ──────────────────────────────────────────────────────────────
  const paid = Object.values(g.markets || {}).filter((m: any) => Eng.hasPaid(m)).length;
  const hasFlagship = Object.values(g.markets || {}).some((m: any) => m.flagship);
  const worldCov = last
    ? last.coverageAvg
    : active.reduce((a, c) => a + Eng.countryCoverage(g, c, round) * Eng.qDemand(c, round), 0) / Eng.worldDemand(round);

  const kpiStrip = () => (
    <div className="grid4" style={{ marginBottom: 14 }}>
      <div className="card"><div className="sub">Paid-distribution markets</div><div className="big-num">{paid}<span className="sub"> /{active.length}</span></div></div>
      <div className="card"><div className="sub">Own stores{hasFlagship ? ' · ⭐ flagship' : ''}</div><div className="big-num">{Eng.totalStores(g)}</div><div className="sub">$70k/qtr each</div></div>
      <div className="card"><div className="sub">World coverage (demand-wtd)</div><div className="big-num">{Math.round((worldCov || 0) * 100)}%</div></div>
      <div className="card"><div className="sub">Est. sales lost to coverage gaps (last qtr)</div><div className={'big-num ' + (last && last.unserved > 1e5 ? 'neg' : '')}>{last ? fmtK(last.unserved) : '—'}</div><div className="sub">customers you couldn't reach</div></div>
    </div>
  );

  // ── white space hints ────────────────────────────────────────────────────────
  const whiteSpaceCard = () => {
    const ws = Eng.whiteSpace(g, round) as any[];
    if (!ws.length) return null;
    return (
      <div className="card" style={{ marginBottom: 14, borderColor: 'rgb(var(--c-good) / .3)' }}>
        <h3>🎯 White space — biggest uncontested cells</h3>
        <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
          {ws.map((w, i) => (
            <span key={i} className="chip good" style={{ cursor: 'pointer', padding: '6px 12px' }} onClick={() => setOpenCC(w.c.id)}>
              <Flag cc={w.c.id} country={w.c.name} />{w.c.name} × {w.seg.icon} {w.seg.name} · {fmtK(w.D)}/qtr · you reach {Math.round(w.mine * 100)}%
            </span>
          ))}
        </div>
        <div className="sub" style={{ marginTop: 8 }}>No competitor has meaningful distribution in these country × segment cells.</div>
      </div>
    );
  };

  // ── filters ────────────────────────────────────────────────────────────────
  const fTabs: [Filter, string][] = [['all', 'All markets'], ['mine', 'My markets'], ['open', 'Uncontested']];
  const filtersCard = () => (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
        {fTabs.map(([id, l]) => <button key={id} className={'opt-pill' + (f === id ? ' on' : '')} onClick={() => setF(id)}>{l}</button>)}
        <span style={{ width: 10 }} />
        {([['all', '🌍 All regions'], ...REGS.map((r) => [r.id, r.name])] as [string, string][]).map(([id, l]) => (
          <button key={id} className={'opt-pill' + (region === id ? ' on' : '')} onClick={() => setRegion(id)}>{l}</button>
        ))}
        <input className="di-search" style={{ marginLeft: 'auto', width: 170 }} placeholder="Search country…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
    </div>
  );

  // ── table ────────────────────────────────────────────────────────────────────
  const sortKey = (c: any): number =>
    sort === 'pop' ? c.pop : sort === 'cov' ? Eng.countryCoverage(g, c, round) : sort === 'share' ? (shareOf(c.id) || 0) : sort === 'brand' ? brandOf(c) : Eng.qDemand(c, round);

  const list = active
    .filter((c) => {
      if (region !== 'all' && c.region !== region) return false;
      if (q && !c.name.toLowerCase().includes(q.toLowerCase())) return false;
      if (f === 'mine') return Eng.hasPaid((g.markets || {})[c.id]);
      if (f === 'open') return Math.max(...comps.map((k) => Eng.compCountryCov(k, c, round))) < 0.3;
      return true;
    })
    .sort((a, b) => sortKey(b) - sortKey(a));

  // Plain render function (a component defined here would remount the table
  // head on every render and lose focus/scroll).
  const th = (id: Sort, label: string) => (
    <th key={id} className="num" style={{ cursor: 'pointer', color: sort === id ? 'var(--acc)' : undefined }} onClick={() => setSort(id)}>{label}{sort === id ? ' ↓' : ''}</th>
  );

  const rowFor = (c: any) => {
    const m = (g.markets || {})[c.id] || {};
    const cov = Eng.countryCoverage(g, c, round);
    const ctrl = controlOf(c);
    const sh = shareOf(c.id);
    const br = brandOf(c);
    return (
      <tr key={c.id} className="row-click" onClick={() => setOpenCC(c.id)}>
        <td>
          <b><Flag cc={c.id} country={c.name} />{c.name}</b>
          <div className="sub">{REGS.find((r) => r.id === c.region)?.name} · income ×{c.income.toFixed(2)}</div>
        </td>
        <td className="num">{c.pop}M</td>
        <td className="num">{fmtK(Eng.qDemand(c, round))}</td>
        <td>{methodsStr(m)}</td>
        <td className="num" style={{ minWidth: 92 }} title="Your Brand Rating here = 0.6 × loyalty + 40 × awareness — campaigns build it only where they run (see Marketing)">
          <div className="spread" style={{ gap: 7 }}><div className="bar-track" style={{ flex: 1, minWidth: 42 }}><div className="bar-fill" style={{ width: Math.round(br) + '%', background: 'var(--acc2)' }} /></div><b>{Math.round(br)}</b></div>
        </td>
        <td className="num" style={{ minWidth: 110 }}>
          <div className="spread" style={{ gap: 8 }}><div className="bar-track" style={{ flex: 1, minWidth: 54 }}><div className="bar-fill" style={{ width: Math.round(cov * 100) + '%', background: cov < 0.3 ? 'var(--warn)' : undefined }} /></div><b>{Math.round(cov * 100)}%</b></div>
        </td>
        <td className="num">{sh != null ? (sh * 100).toFixed(1) + '%' : '—'}</td>
        <td>{ctrlChip(ctrl)}</td>
        <td>
          {comps.map((k) => {
            const p = Eng.compPresence(k, c.id, round);
            const cc2 = Eng.compCountryCov(k, c, round);
            const mObj = Eng.method(p && p.m !== undefined ? p.m : 'dist');
            const meth = p ? (mObj ? mObj.icon + ' ' + mObj.name : '—') : 'no presence';
            return <span key={k.id} className="dot" style={{ background: k.color, opacity: p && !p.cheap ? 0.95 : cc2 > 0.25 ? 0.55 : 0.18 }} title={`${k.name}: ${meth} · est. coverage ${Math.round(cc2 * 100)}%`} />;
          })}
        </td>
      </tr>
    );
  };

  const grouped = region === 'all' && sort === 'size' && !q && f === 'all';

  const tableCard = () => (
    <div className="card">
      <table className="tbl">
        <thead>
          <tr>
            <th>Country</th>
            {th('pop', 'Population')}
            {th('size', 'Mkt size/qtr')}
            <th>Your methods</th>
            {th('brand', 'Your brand' + (unique ? ' (best product)' : ''))}
            {th('cov', 'Coverage')}
            {th('share', 'Your share')}
            <th>Mkt control</th>
            <th>Competitors</th>
          </tr>
        </thead>
        <tbody>
          {grouped
            ? REGS.flatMap((r) => {
                const cs = list.filter((c) => c.region === r.id);
                if (!cs.length) return [];
                return [
                  <tr key={'h-' + r.id}><td colSpan={9} style={{ padding: '12px 10px 4px', color: 'var(--acc2)', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em' }}>{r.name} · {cs.length}</td></tr>,
                  ...cs.map(rowFor),
                ];
              })
            : list.map(rowFor)}
        </tbody>
      </table>
      {list.length ? null : <div className="empty-state">No countries match the filter.</div>}
    </div>
  );

  // ── per-country modal ─────────────────────────────────────────────────────────
  const countryModal = () => {
    if (!openCC) return null;
    const c = Eng.country(openCC);
    if (!c) return null;
    const m = (g.markets || {})[openCC] || {};
    const sm = (server.markets || {})[openCC] || {};
    const cm = last && last.byCountry ? last.byCountry[openCC] : null;
    const needed = Eng.storesNeeded(c, round);
    const sCov = Eng.storeCov(g, c, round);
    const ctrl = controlOf(c);
    const cov = Eng.countryCoverage(g, c, round);
    const maxPot = Math.max(...SEGS.map((s) => Eng.potBuyers(c, s.id)));
    const stagedStores = storeBuildOf(draft, openCC);
    const stagedCloses = storeCloseOf(draft, openCC);

    const methodRow = (mt: any) => {
      const on = mt.id === 'online' || (mt.id === 'dist' && m.dist) || (mt.id === 'stores' && m.sub) || (mt.id === 'carrier' && m.carrier);
      const established = mt.id === 'dist' ? !!sm.dist : mt.id === 'stores' ? !!sm.sub : mt.id === 'carrier' ? !!sm.carrier : false;
      const stagedOn = distHas(draft, openCC, mt.id as DistMethod);
      let right: any;
      if (mt.id === 'online') right = <span className="chip acc">Always on · cap {Math.round(c.ecom * 45)}%</span>;
      else if (established) right = <span className="chip good" title="Locked in when the quarter closed — distribution commitments can't be unwound.">✓ Established</span>;
      else if (stagedOn) right = <button className="btn btn-ghost btn-sm" title="Unstage before submitting for a full refund" onClick={() => toggleDistribution(openCC, mt.id as DistMethod)}>Cancel — refund {fmtM(mt.entry)}</button>;
      else right = <button className="btn btn-ghost btn-sm" disabled={g.cash < mt.entry} onClick={() => toggleDistribution(openCC, mt.id as DistMethod)}>Activate — {fmtM(mt.entry)}</button>;
      return (
        <div key={mt.id} style={{ padding: '10px 12px', border: '1px solid ' + (on ? 'var(--acc)' : 'var(--field-b)'), borderRadius: 10, background: on ? 'rgb(var(--c-acc) / .06)' : 'var(--field)' }}>
          <div className="spread"><b>{mt.icon} {mt.name}</b>{right}</div>
          <div className="sub" style={{ marginTop: 3 }}>{mt.desc}</div>
        </div>
      );
    };

    return (
      <div className="di-modal-bg" onClick={() => setOpenCC(null)}>
        <div className="di-modal" onClick={(e) => e.stopPropagation()}>
          <div className="di-modal-head">
            <h3>{c.name}<span className="chip" style={{ marginLeft: 8, fontSize: 10 }}>{REGS.find((r) => r.id === c.region)?.name}</span></h3>
            <button className="di-modal-x" onClick={() => setOpenCC(null)} aria-label="Close">✕</button>
          </div>
          <div className="di-modal-body">
            <div className="grid3" style={{ gap: 8, marginBottom: 14 }}>
              <div><div className="sub">Population / potential buyers</div><b>{c.pop}M / {fmtK(Eng.potBuyers(c))}</b></div>
              <div><div className="sub">Demand · income · e-com</div><b>{fmtK(Eng.qDemand(c, round))}/qtr · ×{c.income.toFixed(2)} · {Math.round(c.ecom * 100)}%</b></div>
              <div><div className="sub">Your coverage · control</div><b>{Math.round(cov * 100)}%</b> · {ctrlChip(ctrl, 'Full control', 'Partial', 'Distributor-led')}</div>
            </div>
            {ctrl <= 0.5 && Eng.hasPaid(m) ? (
              <div className="sub" style={{ marginBottom: 12, color: 'var(--warn)' }}>⚠ This market is mostly distributor-served: your ad campaigns don't apply here and sales build your brand at half rate.</div>
            ) : null}

            <div className="card" style={{ marginBottom: 14 }}>
              <h3>Segment potential — {String(c.profile).replace('empremium', 'emerging-premium')} profile</h3>
              {SEGS.map((s) => {
                const pot = Eng.potBuyers(c, s.id), covS = Eng.coverage(g, c, s.id, round);
                return (
                  <div key={s.id} style={{ marginBottom: 8 }}>
                    <div className="spread" style={{ fontSize: 12, marginBottom: 2 }}><span>{s.icon} {s.name} <span className="sub">· {fmtK(pot)} buyers · {fmtK(Eng.segDemand(c, s.id, round))}/qtr</span></span><b>{Math.round(covS * 100)}% reach</b></div>
                    <Bar v={(pot / maxPot) * 100} color={s.color} />
                  </div>
                );
              })}
            </div>

            <div className="card" style={{ marginBottom: 14 }}>
              <h3>Distribution methods</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{METHS.map(methodRow)}</div>
            </div>

            {m.sub ? (
              <div className="card" style={{ marginBottom: 14, borderColor: 'rgb(var(--c-acc2) / .35)' }}>
                <h3>🏪 Store network in {c.name}</h3>
                <div className="row" style={{ gap: 14, marginBottom: 10 }}>
                  <span className="sub">Stores</span>
                  <button className="btn btn-ghost btn-sm" disabled={stagedStores <= 0 && !(m.n > 0)}
                    onClick={() => { if (stagedStores > 0) setStoreBuild(openCC, stagedStores - 1); else setStoreClose(openCC, stagedCloses + 1); }}>−</button>
                  <b style={{ fontSize: 18 }}>{m.n || 0}</b>
                  <button className="btn btn-ghost btn-sm" disabled={stagedCloses <= 0 && (stagedStores >= STORE_BUILD_LIMIT || g.cash < 3.5e5)}
                    onClick={() => { if (stagedCloses > 0) setStoreClose(openCC, stagedCloses - 1); else setStoreBuild(openCC, Math.min(STORE_BUILD_LIMIT, stagedStores + 1)); }}>＋</button>
                  <span className="sub">$350k capex each · staged {stagedStores}/{STORE_BUILD_LIMIT} this qtr{stagedCloses > 0 ? ' · closing ' + stagedCloses + ' ($60k severance each)' : ' · closing costs $60k severance'}</span>
                </div>
                <div className="spread" style={{ fontSize: 12, marginBottom: 3 }}><span>Full coverage needs <b>{needed}</b> stores (grows with the market)</span><b>{Math.round(sCov * 100)}% customer reach</b></div>
                <Bar v={sCov * 100} />
                {sCov < 0.93
                  ? <div className="sub" style={{ color: 'var(--warn)', marginTop: 7 }}>⚠ {Math.round((1 - sCov) * 100)}% of {c.name}'s potential customers cannot buy through this channel.</div>
                  : <div className="sub" style={{ color: 'var(--good)', marginTop: 7 }}>✓ Near-full physical coverage.</div>}
                <div className="row" style={{ marginTop: 10, gap: 10 }}>
                  {m.flagship
                    ? <span className="chip acc">⭐ Flagship store — boosts Professionals &amp; Style Icons reach, +brand{flagshipHas(draft, openCC) ? <span style={{ cursor: 'pointer', color: 'var(--bad)', marginLeft: 6 }} title="Unstage" onClick={() => toggleFlagship(openCC)}>✕</span> : null}</span>
                    : <>
                        <button className="btn btn-ghost btn-sm" disabled={!(g.brand >= FLAGSHIP_BRAND && g.cash >= FLAGSHIP_COST && (m.n || 0) > 0)} onClick={() => toggleFlagship(openCC)}>⭐ Designate flagship — {fmtM(FLAGSHIP_COST)}</button>
                        {g.brand < FLAGSHIP_BRAND ? <span className="sub">needs loyalty ≥ {FLAGSHIP_BRAND} (now {Math.round(g.brand)})</span> : !(m.n > 0) ? <span className="sub">needs at least one store here</span> : null}
                      </>}
                </div>
              </div>
            ) : null}

            <div className="card" style={{ marginBottom: 6 }}>
              <h3>🕵️ Competitor distribution here</h3>
              <table className="tbl">
                <thead><tr><th>Rival</th><th>Method</th><th className="num">Est. coverage</th><th className="num">Price</th></tr></thead>
                <tbody>
                  {comps.map((k) => {
                    const p = Eng.compPresence(k, openCC, round), st2 = Eng.competitorState(k, round);
                    const cc2 = Eng.compCountryCov(k, c, round);
                    const meth = p ? (p.cheap ? '🤝 budget distributor + online' : Eng.method(p.m).icon + ' ' + Eng.method(p.m).name) : '— gray imports only';
                    return <tr key={k.id}><td><b style={{ color: k.color }}>{k.name}</b></td><td>{meth}</td><td className="num">{Math.round(cc2 * 100)}%</td><td className="num">{fmt$(st2.price)}</td></tr>;
                  })}
                </tbody>
              </table>
              {cm ? (
                <div className="sub" style={{ marginTop: 8 }}>Last quarter here: you sold <b>{fmtK(cm.units)}</b> of {fmtK(cm.demand)} demand{cm.unserved > 1000 ? <> · est. <b className="neg">{fmtK(cm.unserved)}</b> unserved for lack of coverage</> : null}.</div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="di-root">
      <div className="screen-head">
        <h2>🗺️ Distribution<WhyChip /></h2>
        <div className="sub">{active.length} country markets. Online reaches everyone weakly; distributors are fast but take 28% and your campaigns don't apply; own stores cost capital but give control. Click a country to manage it.</div>
      </div>

      {kpiStrip()}
      {whiteSpaceCard()}
      {filtersCard()}
      {tableCard()}
      <div className="sub" style={{ marginTop: 10 }}>Sort: Market size / Population / Your brand / Your coverage / Your share — click column headers. Competitor dots: bright = launched footprint, faint = online/gray imports only.</div>

      {countryModal()}
    </div>
  );
}
